import { createContext, useState, useContext, ReactNode, useEffect } from "react";
import {CollectionClip, LogicClipDescription} from "../types/collectionTypes";
import {PANEL_MODE} from "../types/generic";
import {KeyMomentCut, tempKeyMoment} from "../types/keyMomentTypes";
import {
    DEFAULT_SORT_FIELD,
    CURRENT_SPORT,
    SportContent,
    SUPPORTED_CONTENTS,
    SUPPORTED_SPORTS,
    SELECTION_LIMIT_MINUTES, lockedKeyMomentTypes
} from "../constants/globalConstants";
import {getCardSelectionDuration} from "../utils/types/cardUtils";
import {enqueueSnackbar} from "notistack";

// Tipi per il contesto
interface SelectedCardContextType {
    selectedCard: CollectionClip[];
    handleSelectedCard: (el: CollectionClip) => void;
    clearSelectedCard: () => void;
    setSelectedCard: (cards: CollectionClip[]) => void;
    removeSelectedCard: (cardId: string) => void;
    toggleLockedState: (cardId: string) => void;
    lockedKeyMoments:  Record<string, boolean>
    lockKeyMoments: (keyMoments: CollectionClip[], contentType: string) => void
    excludedFilters: string[]
    setExcludedFilters: (el: string[]) => void;
    previousPage: string;
    setPreviousPage: (previousPage: string) => void;
    filtersDrawerOpen: boolean;
    setFiltersDrawerOpen: (opened: boolean) => void;
    panelMode: PANEL_MODE;
    setPanelMode: (mode: PANEL_MODE) => void;
    logicClip: CollectionClip | null;
    setLogicClip: React.Dispatch<React.SetStateAction<CollectionClip | null>>;
    logicClipName: string
    setLogicClipName: (name: string) => void,
    updateSelectionCards: (newKeyMomentCuts: KeyMomentCut[], single?: boolean) => tempKeyMoment[];
    getSelectedKeyMomentCutId: (keyMomentId: string) => void;
    addClipCard: (card: CollectionClip) => void;
    addKeyMomentCard: (card: CollectionClip) => void;
    selectedClipCards: CollectionClip[];
    selectedKeyMomentCards: CollectionClip[];
    clearClipCards: () => void
    clearKeyMomentCards: () => void
    resetCardContext: () => void;
    resetSelectionContext: () => void;
    eventId: string | null
    setEventId: (eventId: string | null) => void
    contentType: SportContent<SUPPORTED_SPORTS>,
    setContentType: (contentType: SportContent<SUPPORTED_SPORTS>) => void;
    updateLogicClipDescription: (logicClipDescriptions: LogicClipDescription[]) => void;
    getEditorKeyMoments: () => {key_moment_cut_id: string, key_moment_id: string}[]
    sortOptions: {sortBy: string | null, sortOrder: 'asc' | 'desc'}
    setSortOptions: (newSortingParams: {sortBy: string | null, sortOrder: 'asc' | 'desc'}) => void;
    removeKeyMomentCard: (keyMomentId: string) => void;
    removeClipCard: (clipId: string) => void;
}

// Creazione del contesto
const SelectedCardContext = createContext<SelectedCardContextType | undefined>(undefined);

// Hook personalizzato
export const useSelectedCard = (): SelectedCardContextType => {
    const context = useContext(SelectedCardContext);
    if (!context) {
        throw new Error("useSelectedCard must be used within a SelectedCardProvider");
    }
    return context;
};

// Tipi per le props del provider
interface SelectedCardProviderProps {
    children: ReactNode;
}

export const mapCardToEditorKeyMoment = (card: CollectionClip) => {
    return (card.key_moments || []).map(cut => ({
        key_moment_cut_id: cut.cut_id,
        key_moment_id: card.id
    }));
};

// Provider
export const SelectedCardProvider: React.FC<SelectedCardProviderProps> = ({ children }) => {  
    const [selectedCard, setSelectedCard] = useState<CollectionClip[]>([]);
    const [selectedClipCards, setSelectedClipCards] = useState<CollectionClip[]>([]);
    const [selectedKeyMomentCards, setSelectedKeyMomentCards] = useState<CollectionClip[]>([]);
    const [excludedFilters, setExcludedFilters] = useState<string[]>([]);
    const [previousPage, setPreviousPage] = useState<string>('');
    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState<boolean>(true);
    const [panelMode, setPanelMode] = useState<PANEL_MODE>(PANEL_MODE.CREATE);
    const [logicClip, setLogicClip] = useState<CollectionClip | null>(null);
    const [logicClipName, setLogicClipName] = useState<string>('');
    const [eventId, setEventId] = useState<string | null>(null);
    const currentSortField = DEFAULT_SORT_FIELD[CURRENT_SPORT as keyof typeof SUPPORTED_CONTENTS];
    const [sortOptions, setSortOptions] = useState<{sortBy: string | null, sortOrder: 'asc' | 'desc'}>({
        sortBy: currentSortField?.local?.id ?? null,
        sortOrder: 'asc'
    })
    const [contentType, setContentType] = useState<SportContent<SUPPORTED_SPORTS>>(SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_CONTENTS].CLIP)
    const [lockedKeyMoments, setLockedKeyMoments] = useState<Record<string, boolean>>({})

    const handleSelectedCard = (el: CollectionClip) => {
        setSelectedCard(prev => {
        const exists = prev.some(card => card.id === el.id);
        if (exists) {
            return prev.filter(card => card.id !== el.id);
        } else {
            const {cutDuration, originalDuration} = getCardSelectionDuration([...prev, el], SELECTION_LIMIT_MINUTES)
            if (cutDuration !== originalDuration) {
                enqueueSnackbar(`Aggregated content is now limited to a maximum duration of minutes ${SELECTION_LIMIT_MINUTES}`)
                return prev;
            }else{
                return [...prev, el];
            }
        }
        });
    };

    const addClipCard = (el: CollectionClip) => {
        setSelectedClipCards(prev => {
            const exists = prev.some(card => card.id === el.id);
            if (exists) {
                return prev.filter(card => card.id !== el.id);
            } else {
                return [...prev, el];
            }
        });
    };

    const removeClipCard = (clipId: string) => {
        setSelectedClipCards(prev => prev.filter(clip => clip.id !== clipId));
    }

    const addKeyMomentCard = (el: CollectionClip) => {
        setSelectedKeyMomentCards(prev => {
            const exists = prev.some(card => card.id === el.id)
            if(exists) {
                return prev.filter(card => card.id !== el.id)
            }
            else {
                return [...prev, el]
            }
        })
    }

    const removeKeyMomentCard = (keyMomentId: string) => {
        setSelectedKeyMomentCards(prev => prev.filter(km => km.id !== keyMomentId));
    }

    const clearSelectedCard = () => {
        setSelectedCard([]);
    };

    const clearClipCards = () => {
        setSelectedClipCards([]);
    };

    const clearKeyMomentCards = () => {
        setSelectedKeyMomentCards([]);
    }

    const removeSelectedCard = (id: string) => {
        setSelectedCard((prev) =>
            prev.filter((item) => item.id !== id)
        );
    };

    const toggleLockedState = (id: string) => {
        setLockedKeyMoments(prev => {
            return prev[id] ? {...prev, [id]: false} : {...prev, [id]: true};
        })
    }

    const lockKeyMoments = (keyMoments: CollectionClip[], contentType: string) => {
        const lockedTypes = (lockedKeyMomentTypes as any)[CURRENT_SPORT][contentType]
        const tempLockedKeyMoments: Record<string, boolean> = {}
        for (const keyMoment of keyMoments) {
            if(lockedTypes.includes(keyMoment.title)){
                tempLockedKeyMoments[keyMoment.id] = true
            }
        }
        setLockedKeyMoments(tempLockedKeyMoments);
    }

    const updateSelectionCards = (newKeyMomentCuts: any[], single = false) => {
        const reorderedCards: any[] = [];
        const remainingCards = [...selectedCard];

        for (const newKeyMomentCut of newKeyMomentCuts) {
            let kmTarget = reorderedCards.find(card => card.id === newKeyMomentCut.key_moment_id);
            let isAlreadyReordered = !!kmTarget;

            if (!kmTarget) {
                const targetIndex = remainingCards.findIndex(card => card.id === newKeyMomentCut.key_moment_id);
                if (targetIndex !== -1) {
                    kmTarget = { ...remainingCards.splice(targetIndex, 1)[0] };
                }
            }

            if (kmTarget && kmTarget.key_moments && kmTarget.key_moments.length > 0) {
                const newTrimPoints = newKeyMomentCut.trim_points || [];
                const newLandmarks = newKeyMomentCut.landmarks || [];

                const mappedCuts = newTrimPoints.map((tp: any[], tpIndex: number) => ({
                    start: tp[0],
                    end: tp[1],
                    label: newKeyMomentCut.trim_labels[tpIndex]
                }));

                if (isAlreadyReordered) {
                    const existingCutData = kmTarget.key_moments[0];
                    existingCutData.cuts = [...(existingCutData.cuts || []), ...mappedCuts];
                    existingCutData.landmarks = [...(existingCutData.landmarks || []), ...newLandmarks];
                    existingCutData.plat_cut = newKeyMomentCut
                } else {
                    const newCardCut: any = {
                        cut_id: newKeyMomentCut.id,
                        url: kmTarget.key_moments[0].url,
                        cuts: mappedCuts,
                        landmarks: [...newLandmarks],
                        plat_cut: newKeyMomentCut,
                        stream_reference: newKeyMomentCut.stream_reference,
                    };

                    kmTarget.key_moments = [newCardCut];
                    reorderedCards.push(kmTarget);
                }
            } else if (!kmTarget) {
                console.error("No key moment with id", newKeyMomentCut.key_moment_id, "in selection cards");
            }
        }

        const finalCards = [...reorderedCards, ...remainingCards];

        setSelectedCard(finalCards);

        return finalCards;
    }

    const getSelectedKeyMomentCutId = (keyMomentId: string) =>{
        const targetKeyMoment = selectedCard.find(card => card.id === keyMomentId)
        if(targetKeyMoment){
            return targetKeyMoment.key_moments ? targetKeyMoment.key_moments[0].cut_id : undefined
        }
        return targetKeyMoment;
    }

    const resetCardContext = () => {
        setSelectedCard([]);
        setSelectedClipCards([]);
        setSelectedKeyMomentCards([])
        setExcludedFilters([]);
        setLogicClip(null)
        setLogicClipName('');
        setPanelMode(PANEL_MODE.CREATE);
        setPreviousPage('');
    }

    const resetSelectionContext = () => {
        setSelectedCard([])
        setSelectedKeyMomentCards([])
        setSelectedClipCards([])
    }

    const updateLogicClipDescription = (logicClipDescriptions: LogicClipDescription[]) => {
        if (logicClip){
            const filteredCurrent = (logicClip.generated_descriptions || []).filter(current =>
                !logicClipDescriptions.some(newItem =>
                    newItem.style_name === current.style_name &&
                    newItem.language === current.language
                )
            );
            setLogicClip({...logicClip, generated_descriptions: [...filteredCurrent, ...logicClipDescriptions]})
        }else{
            console.error("You are trying to update the description into a null logic clip context")
        }
    }

    const getEditorKeyMoments = () => {
        return selectedCard.flatMap(card => (card.key_moments || []).map(cut => ({key_moment_cut_id: cut.cut_id, key_moment_id: card.id})))
    }

  return (
    <SelectedCardContext.Provider
      value={{
        selectedCard,
        handleSelectedCard,
        clearSelectedCard,
        setSelectedCard,
        removeSelectedCard,
        lockedKeyMoments,
        toggleLockedState,
        lockKeyMoments,
        excludedFilters,
        setExcludedFilters,
        previousPage,
        setPreviousPage,
        filtersDrawerOpen,
        setFiltersDrawerOpen,
        panelMode,
        setPanelMode,
        logicClip,
        setLogicClip,
        logicClipName,
        setLogicClipName,
        updateSelectionCards,
        getSelectedKeyMomentCutId,
        selectedClipCards,
        selectedKeyMomentCards,
        addKeyMomentCard,
        addClipCard,
        clearClipCards,
        clearKeyMomentCards,
        resetSelectionContext,
        resetCardContext,
        eventId,
        setEventId,
        contentType,
        setContentType,
        updateLogicClipDescription,        
        sortOptions,
        setSortOptions,
        getEditorKeyMoments,
        removeClipCard,
        removeKeyMomentCard
      }}
    >
      {children}
    </SelectedCardContext.Provider>
  );
};
